import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { format, parseISO, subDays } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { FileText, Loader2, Calendar, User, MapPin, Monitor, Clock, Building2, Filter, Image as ImageIcon, Search, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;
axios.defaults.withCredentials = true;

// Helper to format time in EST
const formatTimeEST = (isoString) => {
  const date = parseISO(isoString);
  const estDate = toZonedTime(date, "America/New_York");
  return format(estDate, "h:mm a") + " EST";
};

// Component to display attachments
const AttachmentList = ({ attachments }) => {
  if (!attachments || attachments.length === 0) return null;
  
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {attachments.map((att) => (
        <a
          key={att.id}
          href={`${BASE_URL}${att.url}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded hover:bg-blue-100 transition-colors"
        >
          <ImageIcon className="h-3 w-3" />
          {att.original_name || "Image"}
        </a>
      ))}
    </div>
  );
};

export default function AllLogsPage() {
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [forms, setForms] = useState({});
  const [jdData, setJdData] = useState({});
  const [loading, setLoading] = useState(true);
  const _alf = (() => { try { return JSON.parse(sessionStorage.getItem('jtcrm_all_logs_filters') || '{}'); } catch { return {}; } })();
  const [searchQuery, setSearchQuery] = useState(_alf.searchQuery || "");

  // Default to Today instead of Yesterday — date NOT persisted (always start on today)
  const [selectedDate, setSelectedDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [selectedTeam, setSelectedTeam] = useState(_alf.selectedTeam || "all");
  const [selectedUser, setSelectedUser] = useState(_alf.selectedUser || "all");

  // Get last 14 days for date selection
  const getDateOptions = () => {
    const dates = [];
    for (let i = 0; i <= 14; i++) {
      const date = subDays(new Date(), i);
      dates.push({
        value: format(date, "yyyy-MM-dd"),
        label: i === 0 ? `Today (${format(date, "MMM d")})` : i === 1 ? `Yesterday (${format(date, "MMM d")})` : format(date, "EEEE, MMM d"),
      });
    }
    return dates;
  };

  const dateOptions = getDateOptions();

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [selectedDate, selectedTeam, selectedUser]);

  useEffect(() => {
    sessionStorage.setItem('jtcrm_all_logs_filters', JSON.stringify({ searchQuery, selectedTeam, selectedUser }));
  }, [searchQuery, selectedTeam, selectedUser]);

  const fetchInitialData = async () => {
    try {
      const [usersRes, teamsRes, formsRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/teams`),
        axios.get(`${API}/forms`),
      ]);
      // Include all active users regardless of role, sorted alphabetically
      const activeUsers = usersRes.data.filter((u) => u.is_active);
      activeUsers.sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
      setUsers(activeUsers);
      setTeams(teamsRes.data);
      // Build form lookup by ID
      const formMap = {};
      (formsRes.data || []).forEach(f => { formMap[f.id] = f; });
      setForms(formMap);
    } catch (error) {
      toast.error("Failed to load data");
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("from_date", selectedDate);
      params.append("to_date", selectedDate);
      if (selectedTeam !== "all") {
        params.append("team_id", selectedTeam);
      }
      if (selectedUser !== "all") {
        params.append("user_id", selectedUser);
      }

      const response = await axios.get(`${API}/admin/logs?${params.toString()}`);
      setLogs(response.data);

      // Fetch JobDiva data for the same date
      try {
        const jdRes = await axios.get(`${API}/jobdiva/recruiter-activity?from_date=${selectedDate}&to_date=${selectedDate}`);
        setJdData(jdRes.data?.data || {});
      } catch { setJdData({}); }
    } catch (error) {
      toast.error("Failed to load logs");
    } finally {
      setLoading(false);
    }
  };

  // Filter users by selected team
  const filteredUsers = selectedTeam === "all" 
    ? users 
    : users.filter((u) => u.team_ids && u.team_ids.includes(selectedTeam));

  // Calculate stats - account for selected user filter
  // Exclude exempt users from scope for missing logs calculation
  const usersInScope = selectedUser !== "all" 
    ? users.filter((u) => u.id === selectedUser)
    : filteredUsers;
  const nonExemptUsersInScope = usersInScope.filter((u) => !u.is_exempt);
  const totalUsersInScope = nonExemptUsersInScope.length;
  const submittedCount = logs.length;
  const uniqueUserIds = new Set(logs.map((l) => l.user_id));
  const missingCount = totalUsersInScope - uniqueUserIds.size;

  // Get users who haven't submitted (exclude exempt users)
  const missingUsers = nonExemptUsersInScope.filter((u) => !uniqueUserIds.has(u.id));

  // Get users who have submitted
  const submittedUsers = nonExemptUsersInScope.filter((u) => uniqueUserIds.has(u.id));

  // Apply search filter to logs and missing users
  const searchLower = searchQuery.toLowerCase().trim();
  const filteredLogs = searchQuery 
    ? logs.filter((log) => 
        log.user_name?.toLowerCase().includes(searchLower) || 
        log.user_email?.toLowerCase().includes(searchLower)
      )
    : logs;
  const filteredMissingUsers = searchQuery
    ? missingUsers.filter((u) =>
        u.full_name?.toLowerCase().includes(searchLower) ||
        u.email?.toLowerCase().includes(searchLower)
      )
    : missingUsers;
  const filteredSubmittedUsers = searchQuery
    ? submittedUsers.filter((u) =>
        u.full_name?.toLowerCase().includes(searchLower) ||
        u.email?.toLowerCase().includes(searchLower)
      )
    : submittedUsers;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          All Logs
        </h1>
        <p className="text-slate-600 mt-1">
          View and filter logs across all teams and users
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="w-[220px]">
              <Select value={selectedDate} onValueChange={setSelectedDate}>
                <SelectTrigger data-testid="all-logs-date-selector">
                  <Calendar className="h-4 w-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="Select date" />
                </SelectTrigger>
                <SelectContent>
                  {dateOptions.map((date) => (
                    <SelectItem key={date.value} value={date.value}>
                      {date.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-[200px]">
              <Select value={selectedTeam} onValueChange={(val) => { setSelectedTeam(val); setSelectedUser("all"); }}>
                <SelectTrigger data-testid="all-logs-team-selector">
                  <Building2 className="h-4 w-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="Filter by team" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Teams</SelectItem>
                  {teams.map((team) => (
                    <SelectItem key={team.id} value={team.id}>
                      {team.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-[200px]">
              <Select value={selectedUser} onValueChange={setSelectedUser}>
                <SelectTrigger data-testid="all-logs-user-selector">
                  <User className="h-4 w-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="Filter by user" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  {filteredUsers.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Search by Name */}
            <div className="w-[280px] relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Search by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-8"
                data-testid="all-logs-search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <User className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalUsersInScope}</p>
                <p className="text-sm text-slate-500">Users in Scope</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <FileText className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{submittedCount}</p>
                <p className="text-sm text-slate-500">Logs Submitted</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <FileText className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{missingCount}</p>
                <p className="text-sm text-slate-500">Missing Logs</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {/* Submitted & Missing Users */}
          {selectedUser === "all" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Submitted Users */}
              {filteredSubmittedUsers.length > 0 && (
                <Card className="border-green-100">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-green-700 text-base">
                      Submitted ({filteredSubmittedUsers.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-1.5">
                      {filteredSubmittedUsers.map((user) => (
                        <Badge
                          key={user.id}
                          variant="outline"
                          className="bg-green-50 text-green-700 border-green-200 text-xs cursor-pointer hover:bg-green-100 transition-colors"
                          onClick={() => {
                            const el = document.getElementById(`log-${user.id}`);
                            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          }}
                        >
                          {user.full_name}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Missing Users */}
              {filteredMissingUsers.length > 0 && (
                <Card className="border-red-100">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-red-700 text-base">
                      Missing ({filteredMissingUsers.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-1.5">
                      {filteredMissingUsers.map((user) => (
                        <Badge key={user.id} variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs">
                          {user.full_name}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Submitted Logs */}
          {filteredLogs.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <FileText className="h-12 w-12 text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-700">No logs found</h3>
                <p className="text-slate-500 mt-1">{searchQuery ? `No logs match "${searchQuery}"` : "No logs match your filter criteria"}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {searchQuery && (
                <p className="text-sm text-slate-500">Showing {filteredLogs.length} log{filteredLogs.length !== 1 ? 's' : ''} matching "{searchQuery}"</p>
              )}
              {filteredLogs.map((log) => (
                <Card key={log.id} id={`log-${log.user_id}`} data-testid={`log-detail-${log.id}`}>
                  <CardHeader className="pb-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg flex items-center gap-2 flex-wrap">
                          {log.user_name}
                          {log.team_names && log.team_names.length > 0 && (
                            <div className="flex gap-1">
                              {log.team_names.map((name, idx) => (
                                <Badge key={idx} variant="outline" className="font-normal text-xs">
                                  {name}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </CardTitle>
                        <CardDescription>{log.user_email}</CardDescription>
                      </div>
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        Submitted
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Log Content — dynamic based on form */}
                    <div className="space-y-4">
                      {(() => {
                        const form = forms[log.form_id];
                        const fields = form?.fields || [
                          { id: 'tasks_today', label: 'Tasks Worked On Today', field_type: 'rich_text' },
                          { id: 'challenges', label: 'Challenges and Hurdles', field_type: 'rich_text' },
                          { id: 'plan_tomorrow', label: 'Plan for Tomorrow', field_type: 'rich_text' },
                          { id: 'ai_tools_used', label: 'AI Tools Discovery', field_type: 'rich_text' },
                        ];
                        return fields.filter(f => f.field_type !== 'file').map((field) => {
                          const value = log[field.id];
                          if (!value && value !== 0) return null;
                          const isNumeric = field.field_type === 'number' || field.field_type === 'rating';
                          return (
                            <div key={field.id}>
                              <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
                                {field.label}
                              </h4>
                              {isNumeric ? (
                                <div className="text-sm font-semibold text-slate-800 bg-slate-50 p-3 rounded-lg">
                                  {value}
                                </div>
                              ) : (
                                <div
                                  className="text-sm text-slate-700 bg-slate-50 p-3 rounded-lg prose prose-sm max-w-none"
                                  dangerouslySetInnerHTML={{ __html: value }}
                                />
                              )}
                              <AttachmentList attachments={log[`${field.id}_attachments`]} />
                            </div>
                          );
                        });
                      })()}
                    </div>

                    {/* JobDiva ATS Counts — only for recruiter form users */}
                    {jdData[log.user_id] && (() => {
                      const jd = jdData[log.user_id];
                      const selfSub = parseInt(log.num_submittals || 0);
                      const selfInt = parseInt(log.num_interviews || 0);
                      const hasMismatch = selfSub !== jd.submittals || selfInt !== jd.interviews;
                      return (
                        <div className={`rounded-lg p-3 border ${hasMismatch ? 'bg-amber-50 border-amber-300' : 'bg-indigo-50 border-indigo-200'}`}>
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-semibold text-indigo-700">JobDiva ATS vs Self-Reported</p>
                            {hasMismatch && <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Mismatch</span>}
                          </div>
                          <div className="grid grid-cols-3 gap-3 text-center">
                            <div>
                              <p className={`text-lg font-bold ${selfSub !== jd.submittals ? 'text-amber-700' : 'text-indigo-900'}`}>{jd.submittals}</p>
                              <p className="text-[10px] text-indigo-600 uppercase">JD Submittals</p>
                              {selfSub !== jd.submittals && <p className="text-[10px] text-slate-500">Self: {selfSub}</p>}
                            </div>
                            <div>
                              <p className={`text-lg font-bold ${selfInt !== jd.interviews ? 'text-amber-700' : 'text-indigo-900'}`}>{jd.interviews}</p>
                              <p className="text-[10px] text-indigo-600 uppercase">JD Interviews</p>
                              {selfInt !== jd.interviews && <p className="text-[10px] text-slate-500">Self: {selfInt}</p>}
                            </div>
                            <div>
                              <p className="text-lg font-bold text-indigo-900">{jd.hires}</p>
                              <p className="text-[10px] text-indigo-600 uppercase">JD Hires</p>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Metadata */}
                    <div className="pt-4 border-t border-slate-100">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div className="flex items-center gap-2 text-slate-500">
                          <Clock className="h-4 w-4" />
                          <span>
                            {formatTimeEST(log.created_at)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-slate-500">
                          <Monitor className="h-4 w-4" />
                          <span>{log.browser_name} / {log.os_name}</span>
                        </div>
                        <div className="flex items-center gap-2 text-slate-500">
                          <MapPin className="h-4 w-4" />
                          <span>
                            {[log.geo_city, log.geo_region, log.geo_country].filter(Boolean).join(", ") || log.ip_address}
                          </span>
                        </div>
                        <div className="text-slate-400 text-xs">
                          IP: {log.ip_address}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
